import {Exclude, Expose, instanceToPlain, plainToInstance, Type} from "class-transformer";
import * as t from "io-ts";
import { validateJSON } from "../web/validation";
import { Entity } from "./entity";
import {AddressCodec, AddressEntity} from "./address";

export const WellValidator = t.intersection([
  t.type({
    id: t.string,
    siteId: t.string,
    name: t.string,
    doNotUse: t.boolean
  }),
  t.partial({
    regionList: t.array(t.string),
    externalId: t.string,
    afe: t.string,
    afePrefix: t.string,
    afeSuffix: t.string,
    barcode: t.string,
    siteName: t.string,
    siteCompanyId: t.string,
    leaseId: t.string,
    leaseName: t.string,
    apiNumber: t.string,
    latCoord: t.number,
    longCoord: t.number,
    address: AddressCodec,
    wellStageId: t.string,
    wellStageCode: t.string,
    containerIdList: t.array(t.string),
    createDttm: t.number,
    lastModifiedDttm: t.number
  })
]);

/** Validator for an array of wells. */
export const WellArrayValidator = t.array(WellValidator);

export type Well = t.TypeOf<typeof WellValidator>;
export type WellArray = t.TypeOf<typeof WellArrayValidator>;

@Exclude()
export class WellEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  name!: string;

  @Expose()
  siteId!: string;

  @Expose()
  siteName?: string;

  @Expose()
  siteCompanyId?: string;

  @Expose({ name: "regionList" })
  regionIds?: string[];

  @Expose()
  externalId?: string;

  @Expose()
  afe?: string;

  @Expose()
  afePrefix?: string;

  @Expose()
  afeSuffix?: string;

  @Expose()
  barcode?: string;

  @Expose()
  leaseId?: string;

  @Expose()
  leaseName?: string;

  @Expose()
  apiNumber?: string;

  @Expose()
  latCoord?: number;

  @Expose()
  longCoord?: number;

  @Expose()
  @Type(() => AddressEntity)
  address?: AddressEntity

  @Expose()
  wellStageId?: string;

  @Expose()
  wellStageCode?: string;

  @Expose()
  doNotUse = false;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  readonly entityType = "WELL";

  get displayName(): Readonly<string> {
    return this.name;
  }

  /**
   * Constructor for WellEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  /**
   * Create a WellEntity from a Well type value.
   *
   * @param well the well type value
   */
  static from(well: Well): WellEntity {
    return plainToInstance(WellEntity, well, {
      excludeExtraneousValues: true
    });
  }

  /**
   * Deserializes the WellEntity to a Well type value.
   * If validation fails, the promise will be rejected.
   */
  toTypeValue(): Promise<Well> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true
    });
    return validateJSON(WellValidator, object);
  }
}
